import moment from "moment";

export const patientCostShareAr = {
  templateUrl: "admin/views/patient-cost-share-ar.html",
  bindings: {
    /** Optional: 'detailed' or 'summary'. When set, internal sub-tabs are hidden
     *  and only that view is shown — letting a parent (e.g. the AR page) drive
     *  Detailed/Summary via its own tabs. */
    mode: "<",
    /** Parent AR page date filters. Service range → invoice due_date, invoice
     *  range → generated_at (mapped server-side). Passed as bindings so a remount
     *  (e.g. switching Detailed/Summary tabs) still loads with the current
     *  filters instead of falling back to a default, unfiltered search. */
    serviceDateFrom: "<",
    serviceDateTo: "<",
    invoiceDateFrom: "<",
    invoiceDateTo: "<",
  },
  //! @ngInject
  controller: function (
    $scope,
    $rootScope,
    $filter,
    DatabaseApi,
    toaster,
    NgTableParams,
    ngTableEventsChannel,
    selectionLogic,
    patientLookupService
  ) {
    const $ctrl = this;

    const baseUrl = () =>
      `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}`;

    // Copy the current parent-supplied date-filter bindings into $scope.filters
    // so buildQuery() picks them up. Called on init (so a remount loads filtered)
    // and on the parent's refresh broadcast (Search pressed).
    const syncFiltersFromBindings = () => {
      $scope.filters.serviceDateFrom = $ctrl.serviceDateFrom;
      $scope.filters.serviceDateTo = $ctrl.serviceDateTo;
      $scope.filters.invoiceDateFrom = $ctrl.invoiceDateFrom;
      $scope.filters.invoiceDateTo = $ctrl.invoiceDateTo;
    };

    const toLocalDate = (v) => {
      if (v === null || v === undefined || v === "") return v;
      if (typeof v === "string") return v.length > 10 ? v.slice(0, 10) : v;
      return moment(v).format("YYYY-MM-DD");
    };

    // Cost share is FreedomCare-only.
    $scope.canView = () =>
      $rootScope.isFreedomCareAgency && $rootScope.isPermittedByKey("view_patient_cost_share");

    $scope.subTab = "Detailed";
    $scope.filters = {
      asOfDate: moment().format("YYYY-MM-DD"),
    };
    $scope.detailedData = null;
    $scope.summaryData = null;
    $scope.isLoading = false;

    $scope.dollarsFromCents = (cents) => ((cents || 0) / 100).toFixed(2);

    // Mirrored from the regular AR summary table's "Mark amounts bigger than"
    // widget — value is in dollars (not cents). 0 = no marking.
    $scope.markBiggerThan = 0;


    // Match the regular AR page's global-search pattern: { val: "" } object
    // so the template can two-way bind, and applyGlobalSearch flows the
    // string through NgTable's filter.
    $scope.globalFilter = { val: "" };
    $scope.tableParams = null;

    const FREQUENCY_LABELS = {
      weekly: "Weekly",
      biweekly: "Biweekly",
      monthly: "Monthly",
    };

    const formatLocalDate = (v) => (v ? moment(v).format("MM/DD/YYYY") : "");
    const formatInstant = (v) => (v ? moment(v).format("MM/DD/YYYY") : "");

    // Resolves the patients on this page by id, then decorates. The dictionary of
    // every patient in the agency used to stand in for this.
    const decorateItemsAsync = (items) =>
      patientLookupService
        .byIds(items.map((item) => item.patientId))
        .then((patientsById) => decorateItems(items, patientsById));

    const decorateItems = (items, patientsById) => {
      return items.map((item) => {
        const patient = patientsById.get(String(item.patientId)) || null;
        const balanceCents = item.amountCents - item.paidCents;
        return {
          ...item,
          patient,
          admissionId: patient ? patient.admissionId : null,
          // Sums for the "X selected: $A billed, $B paid, $C balance" header use
          // the current cost-share amount only (base + override). Past balance
          // is rolled forward from prior invoices and must NOT be counted here,
          // so the selected balance shows just the current balance.
          billedAmountCents: item.amountCents,
          paymentCents: item.paidCents,
          balanceCents,
          amountDollars: $scope.dollarsFromCents(item.amountCents),
          pastBalanceDollars: $scope.dollarsFromCents(item.pastBalanceCents),
          paymentDollars: $scope.dollarsFromCents(item.paidCents),
          balanceDollars: $scope.dollarsFromCents(balanceCents),
          outstandingDollars: $scope.dollarsFromCents(item.outstandingCents),
          scheduleFrequencyLabel:
            FREQUENCY_LABELS[item.scheduleFrequency] || item.scheduleFrequency,
          periodLabel: `${formatLocalDate(item.invoicePeriodStart)} – ${formatLocalDate(
            item.invoicePeriodEnd
          )}`,
          generatedAtLabel: formatInstant(item.generatedAt),
          firstPaymentSentAtLabel: item.firstPaymentSentAt
            ? formatInstant(item.firstPaymentSentAt)
            : "",
          mappedId: `cs-invoice-${item.invoiceId}`,
        };
      });
    };

    $scope.applyGlobalSearch = (term) => {
      if (!$scope.tableParams) return;
      // Use the setter form (`filter({...})`) instead of mutating the object
      // returned by the getter — the setter is documented to trigger an
      // ng-table reload, whereas in-place mutation may not be picked up by
      // ng-table's $watch depending on version/binding shape. Merge with the
      // current filter so per-column inputs aren't wiped out.
      $scope.tableParams.filter(
        Object.assign({}, $scope.tableParams.filter(), { $: term || "" })
      );
      // Re-init the selection over the filtered subset so "Select all"
      // operates on what's actually visible — mirrors the regular AR.
      const filteredItems = $filter("filter")($scope.detailedItems, term);
      initSelection(filteredItems);
    };

    // `$scope.detailedItems` is the full decorated list. `$scope.filteredList`
    // is the subset currently visible after ng-table's filter applies — kept
    // in sync via the `onAfterDataFiltered` listener below, and used by the
    // header "Select all" + selection sum.
    $scope.detailedItems = [];
    $scope.filteredList = [];
    $scope.listeningFilterFunction = (_p, _results, data) => {
      $scope.filteredList = data;
    };

    const initSelection = (items) => {
      $scope.selectionLogic = selectionLogic.createNewLogic((item) => {
        $scope.selectionLogic.addItemToCollection(item);
      }, "mappedId");
      if (items) {
        items.forEach((row) => $scope.selectionLogic.initItem(row));
      }
    };

    $scope.getSelectedSums = () => {
      const empty = { billedAmount: 0, paidAmount: 0, balance: 0 };
      if (!$scope.selectionLogic) return empty;
      const sel = $scope.selectionLogic.getSelectedItems();
      let billed = 0;
      let paid = 0;
      for (const s of sel) {
        billed += s.billedAmountCents || 0;
        paid += s.paymentCents || 0;
      }
      return { billedAmount: billed, paidAmount: paid, balance: billed - paid };
    };

    const initDetailedTable = (items) => {
      decorateItemsAsync(items).then((decorated) => {
        $scope.detailedItems = decorated;
        initSelection(decorated);

        $scope.tableParams = new NgTableParams(
          { count: 50 },
          { dataset: decorated }
        );
        $scope.filteredList = decorated;
        ngTableEventsChannel.onAfterDataFiltered($scope.listeningFilterFunction, $scope);

        if ($scope.globalFilter.val) {
          $scope.applyGlobalSearch($scope.globalFilter.val);
        }
      });
    };

    const buildQuery = () => {
      const q = {
        asOfDate: toLocalDate($scope.filters.asOfDate),
        // Service picker → invoice due_date; Invoice picker → generated_at
        // (both mapped server-side).
        serviceDateFrom: toLocalDate($scope.filters.serviceDateFrom),
        serviceDateTo: toLocalDate($scope.filters.serviceDateTo),
        invoiceDateFrom: toLocalDate($scope.filters.invoiceDateFrom),
        invoiceDateTo: toLocalDate($scope.filters.invoiceDateTo),
      };
      const params = Object.entries(q)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
      return params.length > 0 ? `?${params}` : "";
    };

    $scope.loadDetailed = async () => {
      if (!$scope.canView()) return;
      $scope.isLoading = true;
      try {
        const res = await DatabaseApi.get(`${baseUrl()}/patient_cost_share/ar_view${buildQuery()}`);
        $scope.$applyAsync(() => {
          $scope.detailedData = res.data;
          initDetailedTable(res.data.items || []);
        });
      } catch (err) {
        toaster.pop("error", "Failed to load cost-share AR");
        console.log(err);
      } finally {
        $scope.$applyAsync(() => ($scope.isLoading = false));
      }
    };

    /**
     * Renders the column label for a 30-day bucket index:
     *   - 0  → "Current"
     *   - 1  → "1–30"
     *   - 2  → "31–60"
     *   - 3  → "61–90"
     *   - …
     */
    $scope.bucketLabel = (index) => {
      if (index === 0) return "Current";
      return `${(index - 1) * 30 + 1}–${index * 30}`;
    };

    $scope.loadSummary = async () => {
      if (!$scope.canView()) return;
      $scope.isLoading = true;
      try {
        const res = await DatabaseApi.get(
          `${baseUrl()}/patient_cost_share/ar_summary${buildQuery()}`
        );
        $scope.$applyAsync(() => {
          $scope.summaryData = res.data;
          const buckets = res.data.bucketsCents || [];
          $scope.summaryBucketIndices = Array.from(
            { length: buckets.length },
            (_, i) => i
          );
        });
      } catch (err) {
        toaster.pop("error", "Failed to load cost-share AR summary");
        console.log(err);
      } finally {
        $scope.$applyAsync(() => ($scope.isLoading = false));
      }
    };

    $scope.setSubTab = (tab) => {
      $scope.subTab = tab;
      if (tab === "Detailed") {
        $scope.loadDetailed();
      } else {
        $scope.loadSummary();
      }
    };

    $scope.applyFilters = () => {
      if ($scope.subTab === "Detailed") {
        $scope.loadDetailed();
      } else {
        $scope.loadSummary();
      }
    };

    // Parent AR page broadcasts this when its Search button is clicked while
    // cost share is the selected service type — the parent's visit-based
    // query doesn't apply to cost share, so we refresh ourselves.
    // Bind on the component's $scope (not $rootScope) so the listener is
    // automatically deregistered when the component is destroyed — otherwise
    // toggling Detailed/Summary tabs leaks a listener per mount and a single
    // broadcast fires N times.
    $scope.$on("patient_cost_share_ar.refresh", () => {
      syncFiltersFromBindings();
      $scope.applyFilters();
    });

    this.$onInit = async () => {
      $scope.parentDriven = this.mode === "detailed" || this.mode === "summary";
      syncFiltersFromBindings();
      if (this.mode === "summary") {
        $scope.subTab = "Summary";
        await $scope.loadSummary();
      } else {
        $scope.subTab = "Detailed";
        await $scope.loadDetailed();
      }
    };
  },
};
